import * as THREE from 'three';
import type { Airframe } from '../config/airframes';
import { stallSpeedMs } from '../flight/Aerodynamics';
import type { FlightState } from '../flight/FlightState';
import type { Terrain } from '../world/Terrain';
import {
  TagKind,
  type BearingTag,
  type HudFrame,
  type MissileWarning,
  type RwrContact,
} from './HudFrame';

/**
 * Fills a HudFrame from the simulation each frame.
 *
 * This is the seam between the game and the instrument: the HUD knows nothing about the flight
 * model, and the flight model knows nothing about the display. Everything a later milestone adds
 * — real threat sites at M5, missile launches at M6, waypoints at M8, jamming at M5 — arrives by
 * writing into the frame here rather than by reaching into the HUD.
 *
 * The frame object is allocated once and rewritten in place; nothing here allocates per frame.
 */

/**
 * What the threat systems contribute to the display each frame. Assembled by the caller from
 * `ThreatField`, so the HUD keeps knowing nothing about radar and the radar keeps knowing nothing
 * about the HUD.
 */
export interface ThreatTelemetry {
  /** 0–1 jamming severity (SPEC §3.5.4). */
  jamming: number;
  /** 0–1 datalink quality after range, occlusion and jamming. */
  linkQuality: number;
  rwrContacts: readonly RwrContact[];
  missileWarnings: readonly MissileWarning[];
  chaffRemaining: number;
  flaresRemaining: number;
  /** Accumulated GPS position error, metres in world x/z. */
  gpsErrorM: THREE.Vector2;
}

export class HudTelemetry {
  readonly frame: HudFrame;

  private readonly airframe: Airframe;
  private readonly terrain: Terrain;
  /** True world positions of the tags. */
  private readonly tags: BearingTag[] = [];
  /**
   * The tags as *displayed*: the same labels at the positions the navigation system believes
   * them to be. Under GPS denial the two diverge by the accumulated error (SPEC §3.5.4), which is
   * what forces navigation back onto the coastline and the terrain.
   */
  private readonly displayedTags: BearingTag[] = [];

  constructor(airframe: Airframe, terrain: Terrain, frame: HudFrame) {
    this.airframe = airframe;
    this.terrain = terrain;
    this.frame = frame;
    this.frame.bearingTags = this.displayedTags;
  }

  /**
   * Bearing tags come from missions at M8. Until then the display is fed a handful of fixed
   * ground references so the element is visible and tunable — placeholder *data*, not a
   * placeholder implementation.
   */
  addPlaceholderTags(): void {
    // Anchored to real features of the theatre: the port on the bay's north shore, the head of
    // the river valley, and the sand spit across the bay mouth.
    this.addTag(tag('K-2', 'PORT', 4250, 800, TagKind.Threat));
    this.addTag(tag('W-1', 'VALLEY', 7000, 1750, TagKind.Waypoint));
    this.addTag(tag('T-7', 'SPIT', 4150, 2500, TagKind.Target));
  }

  /**
   * Replace the tag set from a mission's route and targets (SPEC §3.10, M8).
   *
   * Until missions existed the display carried three fixed placeholder references that pointed at
   * *nothing the player could attack* — the demo target laydown had no symbology at all, which
   * made a ten-kilometre theatre navigable only by memorising coordinates. A mission's route and
   * its targets are the two things worth a mark on the horizon, so those are what go here.
   */
  setMissionTags(
    waypoints: readonly { id: string; label: string; type: string; positionM: THREE.Vector3 }[],
    targets: readonly {
      designation: string;
      label: string;
      positionM: THREE.Vector3;
      destroyed: boolean;
    }[],
  ): void {
    this.tags.length = 0;
    this.displayedTags.length = 0;
    for (let i = 0; i < waypoints.length; i += 1) {
      const waypoint = waypoints[i];
      const kind = waypoint.type === 'target' ? TagKind.Target : TagKind.Waypoint;
      this.addTag({
        designation: waypoint.id.toUpperCase(),
        type: waypoint.label,
        positionM: waypoint.positionM.clone(),
        kind,
      });
    }
    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i];
      this.addTag({
        designation: target.designation,
        type: target.label,
        positionM: target.positionM.clone(),
        kind: TagKind.Target,
        destroyed: target.destroyed,
      });
    }
    this.frame.bearingTags = this.displayedTags;
  }

  /** Add a navigation or threat tag. */
  addTag(entry: BearingTag): void {
    this.tags.push(entry);
    this.displayedTags.push({
      designation: entry.designation,
      type: entry.type,
      positionM: entry.positionM.clone(),
      kind: entry.kind,
      destroyed: entry.destroyed,
    });
  }

  /** Rewrite the frame from the current flight state and the threat picture. */
  update(state: FlightState, simTimeS: number, threats: ThreatTelemetry): HudFrame {
    const frame = this.frame;

    frame.pitchRad = state.pitchRad;
    frame.bankRad = state.bankRad;
    frame.headingRad = state.headingRad;

    frame.airspeedMs = state.airspeedMs;
    // Radar altitude: height above the terrain beneath, not above sea level (SPEC §3.4.1).
    frame.altitudeAglM = this.terrain.altitudeAglM(state.position);
    frame.loadFactor = state.loadFactor;
    // The commanded lever, not `throttleActual`: see HudFrame.throttle. Read off the flight state
    // rather than the input manager, so the HUD keeps knowing nothing about devices.
    frame.throttle = state.throttleCommand;
    frame.stallSpeedMs = stallSpeedMs(
      this.airframe,
      Math.max(state.airDensityKgM3, 1e-3),
      Math.max(1, Math.abs(state.loadFactor)),
    );

    frame.vneMs = this.airframe.vneMs;

    frame.enduranceS = enduranceSeconds(state, this.airframe);
    frame.linkQuality = threats.linkQuality;
    frame.rwrContacts = threats.rwrContacts;

    // GPS denial: the display shows where the navigation system *believes* each tag is.
    for (let i = 0; i < this.tags.length; i += 1) {
      const truth = this.tags[i].positionM;
      const shown = this.displayedTags[i].positionM;
      shown.set(truth.x + threats.gpsErrorM.x, truth.y, truth.z + threats.gpsErrorM.y);
    }

    frame.missileWarnings = threats.missileWarnings;
    frame.chaffRemaining = threats.chaffRemaining;
    frame.flaresRemaining = threats.flaresRemaining;

    const speed = state.velocity.length();
    frame.velocityValid = speed > 1;
    if (frame.velocityValid) {
      frame.velocityDirection.copy(state.velocity).multiplyScalar(1 / speed);
    }

    frame.simTimeS = simTimeS;
    frame.jamming = threats.jamming;
    return frame;
  }
}

/** Seconds of fuel left at the current burn, or 0 once the engine has stopped. */
export function enduranceSeconds(state: FlightState, airframe: Airframe): number {
  if (!state.engineRunning) return 0;
  const burnKgS = airframe.fuelBurnKgS * Math.max(state.throttleActual, 0.05);
  return state.fuelKg / burnKgS;
}

function tag(
  designation: string,
  type: string,
  x: number,
  z: number,
  kind: (typeof TagKind)[keyof typeof TagKind],
): BearingTag {
  // Ground level: tags anchor to the horizon by azimuth, so their height never matters.
  return { designation, type, positionM: new THREE.Vector3(x, 0, z), kind };
}
